/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Ronnel Bedana - ronnel@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { PresetValue } from '../preset-value';
import { Field, Operand } from '../field';
import { SearchableItem } from '../interface';
import { DocumentType } from '../enums/document.type';
import { CustomQuestion } from '../bvq/custom.question';
import { ReportingType } from '../enums/reporting.type';
import { HolidayDates } from './holiday-dates';

declare var moment: any;

export class Helpers {
  static getDefaultStates(): PresetValue[] {
    return [
      PresetValue.create('Alabama', 'AL'),
      PresetValue.create('Alaska', 'AK'),
      PresetValue.create('Arizona', 'AZ'),
      PresetValue.create('Arkansas', 'AR'),
      PresetValue.create('California', 'CA'),
      PresetValue.create('Colorado', 'CO'),
      PresetValue.create('Connecticut', 'CT'),
      PresetValue.create('Delaware', 'DE'),
      PresetValue.create('District Of Columbia', 'DC'),
      PresetValue.create('Florida', 'FL'),
      PresetValue.create('Georgia', 'GA'),
      PresetValue.create('Hawaii', 'HI'),
      PresetValue.create('Idaho', 'ID'),
      PresetValue.create('Illinois', 'IL'),
      PresetValue.create('Indiana', 'IN'),
      PresetValue.create('Iowa', 'IA'),
      PresetValue.create('Kansas', 'KS'),
      PresetValue.create('Kentucky', 'KY'),
      PresetValue.create('Louisiana', 'LA'),
      PresetValue.create('Maine', 'ME'),
      PresetValue.create('Maryland', 'MD'),
      PresetValue.create('Massachusetts', 'MA'),
      PresetValue.create('Michigan', 'MI'),
      PresetValue.create('Minnesota', 'MN'),
      PresetValue.create('Mississippi', 'MS'),
      PresetValue.create('Missouri', 'MO'),
      PresetValue.create('Montana', 'MT'),
      PresetValue.create('Nebraska', 'NE'),
      PresetValue.create('Nevada', 'NV'),
      PresetValue.create('New Hampshire', 'NH'),
      PresetValue.create('New Jersey', 'NJ'),
      PresetValue.create('New Mexico', 'NM'),
      PresetValue.create('New York', 'NY'),
      PresetValue.create('North Carolina', 'NC'),
      PresetValue.create('North Dakota', 'ND'),
      PresetValue.create('Ohio', 'OH'),
      PresetValue.create('Oklahoma', 'OK'),
      PresetValue.create('Oregon', 'OR'),
      PresetValue.create('Pennsylvania', 'PA'),
      PresetValue.create('Rhode Island', 'RI'),
      PresetValue.create('South Carolina', 'SC'),
      PresetValue.create('South Dakota', 'SD'),
      PresetValue.create('Tennessee', 'TN'),
      PresetValue.create('Texas', 'TX'),
      PresetValue.create('Utah', 'UT'),
      PresetValue.create('Vermont', 'VT'),
      PresetValue.create('Virginia', 'VA'),
      PresetValue.create('Washington', 'WA'),
      PresetValue.create('West Virginia', 'WV'),
      PresetValue.create('Wisconsin', 'WI'),
      PresetValue.create('Wyoming', 'WY')
    ];
  }

  static getFields(reportingType: ReportingType): Field[] {
    if (reportingType == ReportingType.ORDERS) {
      return [
        { KEY: 'COMPANY_NAME', NAME: 'Entity Name', OPERAND: Operand.LIKE },
        { KEY: 'COMPANY_DBA', NAME: 'Entity DBA', OPERAND: Operand.LIKE },
        { KEY: 'STAGE', NAME: 'Stage', OPERAND: Operand.EQUAL },
        { KEY: 'STATE', NAME: 'State', OPERAND: Operand.EQUAL },
        { KEY: 'CLIENT', NAME: 'Client', OPERAND: Operand.EQUAL },
        { KEY: 'ASSIGNEE', NAME: 'Assignee', OPERAND: Operand.EQUAL },
        { KEY: 'TAGS', NAME: 'Tags', OPERAND: Operand.EQUAL },
        { KEY: 'CHARGE_DESC', NAME: 'Rush Fee', OPERAND: Operand.EQUAL },

        { KEY: 'VALUATION_TYPE', NAME: 'Type of Valuation', OPERAND: Operand.EQUAL },
        { KEY: 'CHARGE', NAME: 'Charge', OPERAND: Operand.EQUAL },
        { KEY: 'STANDARD_OF_VALUE', NAME: 'Standard of Value', OPERAND: Operand.EQUAL },
        { KEY: 'PURPOSE', NAME: 'Purpose', OPERAND: Operand.LIKE },

        { KEY: 'DATE_CREATED', NAME: 'Created date', OPERAND: Operand.GREATER_THAN },
        { KEY: 'DATE_UPDATED', NAME: 'Updated date', OPERAND: Operand.GREATER_THAN },
        { KEY: 'DATE_ENGAGED', NAME: 'Engagement Date', OPERAND: Operand.GREATER_THAN },
        { KEY: 'DATE_DUE', NAME: 'Due Date', OPERAND: Operand.LESS_THAN },
        { KEY: 'VALUE_ASSESSMENT', NAME: 'Value Assessment', OPERAND: Operand.EQUAL },
      ];
    }
    else if (reportingType == ReportingType.QUOTES) {
      return [{ KEY: 'COMPANY_NAME', NAME: 'Entity Name', OPERAND: Operand.LIKE },
      { KEY: 'COMPANY_DBA', NAME: 'Entity DBA', OPERAND: Operand.LIKE },
      { KEY: 'STAGE', NAME: 'Stage', OPERAND: Operand.EQUAL },
      { KEY: 'STATE', NAME: 'State', OPERAND: Operand.EQUAL },
      { KEY: 'FIRST_FOLLOW_UP_TIME', NAME: 'First Follow Up Time', OPERAND: Operand.EQUAL },
      { KEY: 'SECOND_FOLLOW_UP_TIME', NAME: 'Second Follow Up Time', OPERAND: Operand.EQUAL },
      { KEY: 'DATE_CREATED', NAME: 'Created Date', OPERAND: Operand.GREATER_THAN },
      { KEY: 'PURPOSE', NAME: 'Purpose', OPERAND: Operand.LIKE },
      { KEY: 'ASSIGNEE', NAME: 'Assignee', OPERAND: Operand.EQUAL },
      { KEY: 'CLIENT_TYPE', NAME: 'Client Type', OPERAND: Operand.EQUAL },
      { KEY: 'CLIENT', NAME: 'Client', OPERAND: Operand.EQUAL },
      { KEY: 'TAGS', NAME: 'Tags', OPERAND: Operand.EQUAL },
      { KEY: 'TURN_FROM', NAME: 'Turn From', OPERAND: Operand.GREATER_THAN },
      { KEY: 'TURN_TO', NAME: 'Turn To', OPERAND: Operand.GREATER_THAN },
      { KEY: 'VALUATION_TYPE', NAME: 'Type of Valuation', OPERAND: Operand.EQUAL },
      { KEY: 'CHARGE', NAME: 'Charge', OPERAND: Operand.EQUAL },
      { KEY: 'CHARGE_DESC', NAME: 'Rush Fee', OPERAND: Operand.EQUAL },
      { KEY: 'REASON_FOR_LOST_OR_WON', NAME: 'Reason', OPERAND: Operand.LIKE },


      ];
    }
    else if (reportingType == ReportingType.ERRORS) {
      return [{ KEY: 'COMPANY_NAME', NAME: 'Entity Name', OPERAND: Operand.LIKE },
      { KEY: 'TIER', NAME: 'Tier', OPERAND: Operand.EQUAL },
      { KEY: 'FINANCIAL_ANALYST', NAME: 'Financial Analyst', OPERAND: Operand.LIKE },
      { KEY: 'ERROR_TYPE', NAME: 'Error Type', OPERAND: Operand.LIKE },

      ];
    }

    else if (reportingType == ReportingType.TIER_RATING) {
      return [
        { KEY: 'ASSIGNEE', NAME: 'Analyst', OPERAND: Operand.EQUAL },
        { KEY: 'COMPANY_NAME', NAME: 'Company Name', OPERAND: Operand.LIKE },
        { KEY: 'RATING', NAME: 'Tier', OPERAND: Operand.EQUAL },
        { KEY: 'ERROR', NAME: 'Error', OPERAND: Operand.EQUAL },
      ];
    }
    else if (reportingType == ReportingType.DROP_OFF) {
      return [
        { KEY: 'BANK_NAME', NAME: 'Bank Name' },
        { KEY: 'PERIOD_LAST_YEAR', NAME: 'Period Last Year' },
        { KEY: 'PERIOD_THIS_YEAR', NAME: 'Period This Year' },
        { KEY: 'CHANGE', NAME: '% Change' },
      ];
    }
    else {
      return [];
    }
  }

  static getDefaultFields(reportingType: ReportingType = ReportingType.ORDERS): Field[] {
    if (reportingType == ReportingType.ERRORS || reportingType == ReportingType.TIER_RATING || reportingType == ReportingType.DROP_OFF) {

      return this.getFields(reportingType)

    }
    else if (reportingType == ReportingType.QUOTES || reportingType == ReportingType.ORDERS) {
      return [
        this.getFields(reportingType)[0],
        this.getFields(reportingType)[1],
        this.getFields(reportingType)[8],
        this.getFields(reportingType)[3],
        this.getFields(reportingType)[12],
        this.getFields(reportingType)[13]
      ];
    }
    else {
      return [];
    }
  }

  static getHistoryFields(): Field[] {
    const historyField: Field[] = this.getFields(ReportingType.ORDERS);
    historyField.push({
      KEY: 'BDO',
      NAME: 'Client BDO'
    });

    historyField.push({
      KEY: 'EMAIL_CC',
      NAME: 'CC on Correspondence'
    });

    historyField.push({
      KEY: 'EMAIL_ENGAGE',
      NAME: 'Receives Engagement Email'
    });

    historyField.push({
      KEY: 'PROCESSOR',
      NAME: 'Reliant Party Processor'
    });

    historyField.push({
      KEY: 'INITIAL_EMAIL',
      NAME: 'Initial Email'
    });

    historyField.push({
      KEY: 'ENGAGED_DS',
      NAME: 'Engage via DosuSignt'
    });

    historyField.push({
      KEY: 'ENGAGED_MANUAL',
      NAME: 'Engage without DosuSign'
    });

    historyField.push({
      KEY: 'ENGAGED_RESEND_MANUAL',
      NAME: ' Re-Send Engage without DosuSign'
    });

    historyField.push({
      KEY: 'SEND_QUOTE',
      NAME: 'Quote Sent'
    });

    historyField.push({
      KEY: 'QUOTE_FOLLOW_UP',
      NAME: 'Quote Follow Up'
    });

    historyField.push({
      KEY: 'UNLOCK',
      NAME: 'Unlock'
    });

    historyField.push({
      KEY: 'FIRST_QUOTE_FOLLOW_UP',
      NAME: 'First Quote Follow Up'
    });

    historyField.push({
      KEY: 'SECOND_QUOTE_FOLLOW_UP',
      NAME: 'Second Quote Follow Up'
    });


    return historyField;
  }


  static getModalConfig(): any {
    const config = {
      backdrop: true,
      ignoreBackdropClick: true
    };

    return config;
  }

  static getDueDate(startDate: Date, days: number): void {
    let date = moment(startDate);
    let daysCount = JSON.parse(JSON.stringify(days));

    // Holidays come from the admin-managed list (HolidayDates), the single source of
    // truth shared with the backend, instead of the moment-holiday library.
    while (daysCount > 0) {
      date.add(1, 'days');
      if (!HolidayDates.isHoliday(date) && date.isoWeekday() !== 6 && date.isoWeekday() !== 7) {
        daysCount--;
      }
    }

    return date.format('DD/MM/YYYY');
  }

  static getEntityTypes(): SearchableItem[] {
    return [
      PresetValue.create('a Sole Proprietorship', 'SOLE_PROPRIETOR'),
      PresetValue.create('a General Partnership', 'GENERAL_PARTNERSHIP'),
      PresetValue.create('a C Corporation', 'C_CORP'),
      PresetValue.create('an S Corporation', 'S_CORP'),
      PresetValue.create('a Limited Liability Company', 'LLC'),
      PresetValue.create('a Limited Liability Company filing as a disregarded entity (single member)', 'LLC_DISREGARDED_ENTITY'),
      PresetValue.create('a Limited Liability Company filing taxes as an S Corporation', 'LLC_S_CORP'),
      PresetValue.create('a Professional Limited Liability Company', 'PROFESSIONAL_LLC'),
      PresetValue.create('a Professional Association filing as an S Corporation', 'PROFESSIONAL_ASSOC_S_CORP'),
    ];
  }

  static getBusinessForms(): any[] {
    return [
      { BUSINESS: 'a Limited Liability Company', FORM: 'Form 1065' },
      { BUSINESS: 'an S Corporation', FORM: 'Form 1120S' },
      { BUSINESS: 'a C Corporation', FORM: 'Form 1120' },
      { BUSINESS: 'a Sole Proprietorship', FORM: 'Form 1040 Schedule C' },
      { BUSINESS: 'a Limited Liability Company filing as a disregarded entity (single member)', FORM: 'Form 1040 Schedule C' },
      { BUSINESS: 'a Limited Liability Company filing taxes as an S Corporation', FORM: 'Form 1120S' },
      { BUSINESS: 'a General Partnership', FORM: 'Form 1065' },
      { BUSINESS: 'a Professional Limited Liability Company', FORM: 'Form 1065' },
      { BUSINESS: 'a Professional Association filing as an S Corporation', FORM: 'Form 1120S' }
    ];
  }

  static getDocuments(): PresetValue[] {
    return [
      PresetValue.create("Engagement Letter", DocumentType.ENGAGEMENT_LETTER),
      PresetValue.create("Income Statement", DocumentType.IS),
      PresetValue.create("Balance Sheet", DocumentType.BS),
      PresetValue.create("Income Statement & Balance Sheet", DocumentType.ISBS),
      PresetValue.create("Purchase Agreement/LOI", DocumentType.PA_LOI),
      PresetValue.create("Write Up", DocumentType.WRITE_UP),
      PresetValue.create("Other", DocumentType.OTHER),
    ];
  }


  static getQuestionTypes(): CustomQuestion[] {
    return [
      CustomQuestion.createQuestion('MLQ', 'Open Ended', 'Sub-Level Open Ended'),
      CustomQuestion.createQuestion('TBL', 'Fill Table', 'Fill in the Table'),
      CustomQuestion.createQuestion('TBS', 'Summary Table', 'Summary Table'),
      CustomQuestion.createQuestion('SNP', 'Snapshot', 'Snapshot'),
      CustomQuestion.createQuestion('SLC', 'S-L Confirmation', 'Sub-level Confirmations'),
      CustomQuestion.createQuestion('SCQ', 'Confirmation', 'Confirmation Questions'),
      CustomQuestion.createQuestion('ROD', 'Documentation', 'Documentation')
    ];
  }

  static getFirstName(name: string): string {
    const arr = name.split(' ');
    if (arr.length === 1) {
      return arr[0];
    }
    return arr.slice(0, -1).join(' ');
  }

  static businessAdd(date: String, days: number): string {

    let engDate = moment(date);

    let daysCount = days;

    while (daysCount > 0) {
      engDate = moment(engDate).add(1, 'days');

      if (engDate.isoWeekday() !== 6 && engDate.isoWeekday() !== 7 && !HolidayDates.isHoliday(engDate))
        daysCount -= 1;
    }


    return moment(engDate).format("MM/DD/YYYY");
  }


  static addBusinessDays(date: string, days: number): string {
    let engDate = moment(date, "MM/DD/YYYY");
    console.log(engDate, "end date")
    let daysCount = days;
    console.log(daysCount, "days count")

    while (daysCount > 0) {
      engDate = moment(engDate).add(1, 'days');

      if (engDate.isoWeekday() !== 6 && engDate.isoWeekday() !== 7 && !HolidayDates.isHoliday(engDate)) {
        daysCount -= 1;
      }
    }

    return engDate.format("MMMM Do, YYYY");
  }

  static getColumns(): any[] {
    return [
      PresetValue.create('Low', 'LOW'),
      PresetValue.create('High', 'HIGH'),
      PresetValue.create('Mean', 'MEAN'),
      PresetValue.create('Median', 'MEDIAN'),
      PresetValue.create('Harmonic Mean', 'HARMONICE_MEAN'),
      PresetValue.create('Standard Deviation', 'STANDARD_DEVIATION'),
      PresetValue.create('Coefficient of Variation', 'COEFICIENT_VARIATION'),
      PresetValue.create('Count', 'COUNT'),
      PresetValue.create('Percentile', 'PERCENTILE')];
  }
  static getPercentileOptions(): any[] {
    return [
      PresetValue.create('5%', 5),
      PresetValue.create('15%', 15),
      PresetValue.create('25%', 25),
      PresetValue.create('35%', 35),
      PresetValue.create('45%', 45),
      PresetValue.create('55%', 55),
      PresetValue.create('65%', 65),
      PresetValue.create('75%', 75),
      PresetValue.create('85%', 85),
      PresetValue.create('95%', 95)
    ];
  }

}
